import { useState, useRef, useEffect } from "react";

interface DemoCall {
  id: string;
  title: string;
  subtitle: string;
  duration: string;
  description: string;
  highlights: string[];
  wavePattern: number[];
  audioSrc?: string;
}

const DEMO_CALLS: DemoCall[] = [
  {
    id: "listing-agent",
    title: "Listing Agent Call",
    subtitle: "New listing triggered outreach",
    duration: "0:52",
    description: "Imperium AI calls a listing agent within a day of the new MLS listing, establishing a relationship before any other title company gets the chance.",
    highlights: ["Identifies listing activity instantly", "Natural, professional tone", "Soft title introduction", "Soft title integration, seller net sheet offered."],
    wavePattern: [30, 55, 42, 78, 62, 45, 88, 70, 55, 40, 72, 85, 60, 48, 92, 75, 58, 44, 80, 68],
    audioSrc: "https://storage.readdy-site.link/project_files/a5e9c8d2-9c4d-45a2-a3e3-a7805868b9ce/896f53cb-b9e2-4cdf-9f0f-3899190fa755_Hi_this_is_Alea_with_Acme_Titl_Bright_Eleven_v3_019d44ef-ee2c-7b16-b291-f5f309b6d36b.mp3?v=03917ad399f36668ee4e53a93f7466c6",
  },
  {
    id: "agent-both-sides",
    title: "Agent on Other Side of the Closing (Non-Directing Agent)",
    subtitle: "Non-directing agent outreach",
    duration: "1:08",
    description: "When the cooperating agent on the other side isn't directing title, Imperium detects this and customizes outreach to position your team for the closing before anyone else reaches them.",
    highlights: ["Identifies non-directing agent scenarios", "Higher-value relationship opening", "Builds trust fast, asks if their client would like to see a closing cost estimate."],
    wavePattern: [45, 70, 58, 85, 48, 90, 65, 72, 55, 80, 42, 68, 92, 58, 75, 48, 85, 62, 78, 50],
    audioSrc: "https://storage.readdy-site.link/project_files/a5e9c8d2-9c4d-45a2-a3e3-a7805868b9ce/057e6394-6ed2-4eab-b43d-3be5849ac715_Hey_Dan_-_This_is_Alice_an_AI_Bright_Eleven_v3_019d456d-63dd-792a-9e70-aa8f03a3f802.mp3?v=904bcb02e4ae2cb1c75e4b8bc070adc6",
  },
];

export default function ProofSection() {
  const [activeCall, setActiveCall] = useState<string | null>(null);
  const [progress, setProgress] = useState<Record<string, number>>({});
  const [displayDuration, setDisplayDuration] = useState<Record<string, string>>({});
  const [currentTime, setCurrentTime] = useState<Record<string, string>>({});

  const audioRef = useRef<HTMLAudioElement | null>(null);
  const simIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  // Format seconds → M:SS
  const formatTime = (secs: number): string => {
    const m = Math.floor(secs / 60);
    const s = Math.floor(secs % 60);
    return `${m}:${s.toString().padStart(2, "0")}`;
  };

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      audioRef.current?.pause();
      if (simIntervalRef.current) clearInterval(simIntervalRef.current);
    };
  }, []);

  const stopAll = () => {
    // Stop real audio
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.currentTime = 0;
      audioRef.current = null;
    }
    // Stop simulated
    if (simIntervalRef.current) {
      clearInterval(simIntervalRef.current);
      simIntervalRef.current = null;
    }
    setActiveCall(null);
  };

  const handlePlay = (call: DemoCall) => {
    // If same card is playing — stop it
    if (activeCall === call.id) {
      stopAll();
      return;
    }

    // Stop whatever was running first
    stopAll();
    setProgress(prev => ({ ...prev, [call.id]: 0 }));
    setCurrentTime(prev => ({ ...prev, [call.id]: "0:00" }));
    setActiveCall(call.id);

    if (call.audioSrc) {
      // Real audio playback
      const audio = new Audio(call.audioSrc);
      audioRef.current = audio;

      audio.addEventListener("loadedmetadata", () => {
        setDisplayDuration(prev => ({ ...prev, [call.id]: formatTime(audio.duration) }));
      });

      audio.addEventListener("timeupdate", () => {
        const dur = audio.duration || 1;
        const pct = (audio.currentTime / dur) * 100;
        setProgress(prev => ({ ...prev, [call.id]: pct }));
        setCurrentTime(prev => ({ ...prev, [call.id]: formatTime(audio.currentTime) }));
      });

      audio.addEventListener("ended", () => {
        setActiveCall(null);
        setProgress(prev => ({ ...prev, [call.id]: 0 }));
        setCurrentTime(prev => ({ ...prev, [call.id]: "0:00" }));
        audioRef.current = null;
      });

      audio.play().catch(() => {
        setActiveCall(null);
      });
    } else {
      // Simulated animation for cards without real audio
      simIntervalRef.current = setInterval(() => {
        setProgress(prev => {
          const current = (prev[call.id] || 0) + 1;
          if (current >= 100) {
            clearInterval(simIntervalRef.current!);
            simIntervalRef.current = null;
            setActiveCall(null);
            return { ...prev, [call.id]: 0 };
          }
          return { ...prev, [call.id]: current };
        });
      }, 400);
    }
  };

  return (
    <section id="demo" className="bg-white py-14 md:py-24 px-6 lg:px-8">
      <div className="max-w-7xl mx-auto">
        <div className="text-center max-w-2xl mx-auto mb-10 md:mb-14">
          <div className="inline-flex items-center gap-2 bg-blue-50 border border-blue-100 rounded-full px-4 py-1.5 mb-6">
            <i className="ri-headphone-line text-secondary text-xs" />
            <span className="text-xs font-semibold text-secondary">Real Conversations</span>
          </div>
          <h2
            className="text-navy font-semibold mb-4"
            style={{ fontSize: "clamp(26px, 3vw, 38px)", lineHeight: "1.2" }}
          >
            Hear It In Action — Real Conversations
          </h2>
          <p className="text-[#4a5568] text-lg" style={{ lineHeight: "1.65" }}>
            These are actual AI-generated calls. This is what agents hear on the other end.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-4xl mx-auto">
          {DEMO_CALLS.map((call) => {
            const isPlaying = activeCall === call.id;
            const prog = progress[call.id] || 0;
            const hasRealAudio = Boolean(call.audioSrc);
            const shownDuration = hasRealAudio && displayDuration[call.id]
              ? displayDuration[call.id]
              : call.duration;
            const shownCurrent = currentTime[call.id] || "0:00";

            return (
              <div
                key={call.id}
                className={`bg-alt-bg rounded-2xl p-6 border transition-all ${
                  isPlaying ? "border-secondary" : "border-gray-100 hover:border-gray-200"
                }`}
              >
                {/* Header */}
                <div className="flex items-start justify-between mb-5">
                  <div>
                    <h3 className="text-navy font-semibold text-base">{call.title}</h3>
                    <p className="text-[#4a5568] text-xs mt-0.5">{call.subtitle}</p>
                  </div>
                  <span className="text-[#4a5568] text-xs font-mono bg-white rounded-lg px-2 py-1 border border-gray-200">
                    {isPlaying && hasRealAudio ? `${shownCurrent} / ${shownDuration}` : shownDuration}
                  </span>
                </div>

                {/* Waveform */}
                <div className="flex items-center gap-0.5 h-10 mb-4">
                  {call.wavePattern.map((h, i) => {
                    const playedFraction = prog / 100;
                    const played = i / call.wavePattern.length <= playedFraction;
                    return (
                      <div
                        key={i}
                        className={`flex-1 rounded-full transition-colors ${
                          played ? "bg-secondary" : isPlaying ? "bg-secondary/30" : "bg-gray-300"
                        }`}
                        style={{ height: `${h}%` }}
                      />
                    );
                  })}
                </div>

                {/* Progress bar */}
                {(isPlaying || prog > 0) && (
                  <div className="w-full h-1 bg-gray-200 rounded-full mb-4 overflow-hidden">
                    <div
                      className="h-full bg-secondary rounded-full transition-all duration-300"
                      style={{ width: `${prog}%` }}
                    />
                  </div>
                )}

                {/* Play button */}
                <button
                  onClick={() => handlePlay(call)}
                  className={`flex items-center gap-2.5 w-full px-4 py-2.5 rounded-xl font-semibold text-sm transition-all cursor-pointer whitespace-nowrap ${
                    isPlaying
                      ? "bg-secondary text-white"
                      : "bg-white border border-gray-200 text-navy hover:border-secondary/50"
                  }`}
                >
                  <span className="w-6 h-6 flex items-center justify-center">
                    <i className={`${isPlaying ? "ri-pause-fill" : "ri-play-fill"} text-base`} />
                  </span>
                  {isPlaying ? "Pause Demo" : "Play Demo Call"}
                  {hasRealAudio && !isPlaying && (
                    <span className="ml-auto flex items-center gap-1 text-[10px] text-secondary font-medium">
                      <span className="w-1.5 h-1.5 rounded-full bg-secondary animate-pulse" />
                      Live Audio
                    </span>
                  )}
                </button>

                {/* Description */}
                <p className="text-[#4a5568] text-xs leading-relaxed mt-4">{call.description}</p>

                {/* Highlights */}
                <div className="flex flex-col gap-1.5 mt-4">
                  {call.highlights.map((h) => (
                    <div key={h} className="flex items-center gap-2">
                      <span className="w-3.5 h-3.5 flex items-center justify-center text-green-500">
                        <i className="ri-check-line text-xs" />
                      </span>
                      <span className="text-[#4a5568] text-xs">{h}</span>
                    </div>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}
